import React, { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  KeyRound,
  Copy,
  Download,
  FileText,
  ShieldAlert,
  ShieldCheck,
  Eye,
  EyeOff,
} from 'lucide-react';
import { generateRecoveryKeyPaperWallet } from '../utils/recoveryKeyPdf';

/**
 * RecoveryKeyDialog
 *
 * Shown exactly ONCE after registration (or password reset). User must:
 *   1. Copy or download the key.
 *   2. Acknowledge they've saved it.
 * Server never shows it again. Losing it means losing account recovery.
 */
export const RecoveryKeyDialog = ({ isOpen, recoveryKey, username, onAcknowledge, context = 'register' }) => {
  const [revealed, setRevealed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey);
      toast.success('Recovery key copied to clipboard');
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const handleDownload = () => {
    const content =
      `Government Shield Recovery Key\n` +
      `========================\n\n` +
      `Account: ${username || '(unknown)'}\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      `Recovery Key:\n${recoveryKey}\n\n` +
      `IMPORTANT:\n` +
      `- Store this file somewhere SAFE and OFFLINE (USB drive, password manager, paper).\n` +
      `- This key is the ONLY way to reset your password if you forget it.\n` +
      `- Government Shield support CANNOT recover this key for you. Lose it = lose your account.\n` +
      `- If you suspect it's been stolen, log in and regenerate it immediately.\n`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ghostcomm-recovery-${(username || 'account').replace(/\W/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Recovery key downloaded');
  };

  const handleDownloadPdf = async () => {
    try {
      await generateRecoveryKeyPaperWallet({ recoveryKey, username });
      toast.success('Paper wallet PDF downloaded — print and store offline');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  // Block Escape / outside-click from dismissing without acknowledgement.
  const handleOpenChange = (next) => {
    if (!next && !acknowledged) {
      toast.error('Please save your recovery key and confirm before continuing');
      return;
    }
    if (!next) onAcknowledge();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-card border-border max-w-lg"
        onEscapeKeyDown={(e) => !acknowledged && e.preventDefault()}
        onPointerDownOutside={(e) => !acknowledged && e.preventDefault()}
        data-testid="recovery-key-dialog"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/10 mx-auto mb-2">
            <KeyRound className="w-7 h-7 text-amber-400" />
          </div>
          <DialogTitle className="text-center text-xl">
            {context === 'reset' ? 'New Recovery Key' : 'Save Your Recovery Key'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {context === 'reset'
              ? 'Your old recovery key is no longer valid. Save this new one now.'
              : 'This is the ONLY way to reset your password. Government Shield cannot recover it for you.'}
          </DialogDescription>
        </DialogHeader>

        {/* Key display */}
        <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wide text-amber-300 font-semibold">
              Recovery Key
            </span>
            <button
              onClick={() => setRevealed((v) => !v)}
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
              data-testid="toggle-recovery-key-visibility"
            >
              {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {revealed ? 'Hide' : 'Reveal'}
            </button>
          </div>
          <div
            className="font-mono text-sm leading-relaxed break-all select-all tracking-wider text-white"
            data-testid="recovery-key-value"
            style={{ filter: revealed ? 'none' : 'blur(6px)' }}
          >
            {recoveryKey}
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-3 gap-2 mt-1">
          <Button
            variant="secondary"
            onClick={handleCopy}
            className="w-full"
            data-testid="recovery-key-copy-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          <Button
            variant="secondary"
            onClick={handleDownload}
            className="w-full"
            data-testid="recovery-key-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            .txt
          </Button>
          <Button
            variant="secondary"
            onClick={handleDownloadPdf}
            className="w-full"
            data-testid="recovery-key-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Paper PDF
          </Button>
        </div>

        {/* Warning */}
        <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-red-300 leading-relaxed">
            <strong>Store offline.</strong> Anyone with this key can reset your password. Do NOT
            email it to yourself, screenshot it to cloud photo storage, or share it.
          </div>
        </div>

        {/* Acknowledge */}
        <div className="mt-2 flex items-start gap-2">
          <Checkbox
            id="recovery-ack"
            checked={acknowledged}
            onCheckedChange={setAcknowledged}
            data-testid="recovery-key-ack-checkbox"
          />
          <label htmlFor="recovery-ack" className="text-sm text-slate-300 cursor-pointer">
            I have saved my recovery key somewhere safe. I understand it will never be shown again
            and that losing it means losing account recovery.
          </label>
        </div>

        <DialogFooter>
          <Button
            onClick={onAcknowledge}
            disabled={!acknowledged}
            className="w-full glow-primary"
            data-testid="recovery-key-continue-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecoveryKeyDialog;
